// @ts-nocheck
/**
 * 📱 MOBILE IMAGE OPTIMIZATION UTILITY
 * Optimizes images for mobile performance with Cloudinary integration
 * Provides lazy loading, progressive enhancement, and bandwidth optimization
 */

interface ImageOptimizationOptions {
  width?: number;
  height?: number;
  quality?: 'auto' | 'low' | 'medium' | 'high';
  format?: 'auto' | 'webp' | 'avif' | 'jpg' | 'png';
  dpr?: number; // Device pixel ratio
  crop?: 'fill' | 'fit' | 'scale' | 'crop';
  gravity?: 'auto' | 'face' | 'center';
  blur?: number;
  progressive?: boolean;
}

interface MobileImageConfig {
  mobile: ImageOptimizationOptions;
  tablet: ImageOptimizationOptions;
  desktop: ImageOptimizationOptions;
}

/**
 * Get optimized image URL for mobile devices
 */
export const getOptimizedImageUrl = (
  url: string | null | undefined,
  options: ImageOptimizationOptions = {}
): string | null => {
  if (!url) return null;

  // Default mobile-first optimization
  const defaultOptions: ImageOptimizationOptions = {
    width: 400,
    height: 400,
    quality: 'auto',
    format: 'auto',
    dpr: window.devicePixelRatio || 2,
    crop: 'fill',
    gravity: 'face',
    progressive: true
  };

  const finalOptions = { ...defaultOptions, ...options };

  // Only optimize Cloudinary URLs
  if (!url.includes('cloudinary.com') && !url.includes('res.cloudinary.com')) {
    return url;
  }

  // Build Cloudinary transformation string
  const transformations = [
    `w_${finalOptions.width}`,
    `h_${finalOptions.height}`,
    `c_${finalOptions.crop}`,
    `f_${finalOptions.format}`,
    `q_${finalOptions.quality}`,
    `dpr_${finalOptions.dpr}`,
    finalOptions.gravity && `g_${finalOptions.gravity}`,
    finalOptions.progressive && 'fl_progressive',
    finalOptions.blur && `e_blur:${finalOptions.blur}`
  ].filter(Boolean).join(',');

  // Insert transformations into Cloudinary URL
  return url.replace('/upload/', `/upload/${transformations}/`);
};

/**
 * Get responsive image URLs for different screen sizes
 */
export const getResponsiveImageUrls = (
  url: string | null | undefined
): MobileImageConfig | null => {
  if (!url) return null;

  return {
    mobile: {
      width: 400,
      height: 400,
      quality: 'auto',
      format: 'auto',
      dpr: 2,
      crop: 'fill',
      gravity: 'face'
    },
    tablet: {
      width: 600,
      height: 600,
      quality: 'auto',
      format: 'auto',
      dpr: 2,
      crop: 'fill',
      gravity: 'face'
    },
    desktop: {
      width: 800,
      height: 800,
      quality: 'auto',
      format: 'auto',
      dpr: 1,
      crop: 'fill',
      gravity: 'face'
    }
  };
};

/**
 * Detect if user is on mobile device
 */
export const isMobileDevice = (): boolean => {
  return window.innerWidth < 768;
};

/**
 * Detect if user is on slow connection
 */
export const isSlowConnection = (): boolean => {
  if ('connection' in navigator) {
    const connection = (navigator as any).connection;
    return connection.effectiveType === 'slow-2g' || 
           connection.effectiveType === '2g' ||
           connection.saveData === true;
  }
  return false;
};

/**
 * Get optimized image URL based on device and connection
 */
export const getAdaptiveImageUrl = (
  url: string | null | undefined
): string | null => {
  if (!url) return null;

  const isMobile = isMobileDevice();
  const isSlowConn = isSlowConnection();

  let options: ImageOptimizationOptions;

  if (isSlowConn) {
    // Ultra-low bandwidth optimization
    options = {
      width: isMobile ? 200 : 400,
      height: isMobile ? 200 : 400,
      quality: 'low',
      format: 'auto',
      dpr: 1,
      crop: 'fill',
      progressive: true
    };
  } else if (isMobile) {
    // Mobile optimization
    options = {
      width: 400,
      height: 400,
      quality: 'auto',
      format: 'auto',
      dpr: 2,
      crop: 'fill',
      progressive: true
    };
  } else {
    // Desktop optimization
    options = {
      width: 800,
      height: 800,
      quality: 'auto',
      format: 'auto',
      dpr: 1,
      crop: 'fill',
      progressive: true
    };
  }

  return getOptimizedImageUrl(url, options);
};

/**
 * Preload critical images for better performance with 404 suppression
 */
export const preloadImage = (url: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    // 🔇 APPLY 404 SUPPRESSION: Same logic as main preloadImage
    const originalConsoleError = console.error;
    const suppressErrors = () => {
      console.error = (...args) => {
        const message = args.join(' ');
        if (message.includes('404') && (message.includes('cloudinary.com') || message.includes('fd3d730e-1b0e-4948-a6bc-bcaaf7f1d9a1'))) {
          console.log(`🔇 imageOptimization suppressed 404: ${url.split('/').pop()}`);
          return;
        }
        originalConsoleError.apply(console, args);
      };
    };

    const restoreConsole = () => {
      console.error = originalConsoleError;
    };

    suppressErrors();

    img.onload = () => {
      restoreConsole();
      resolve();
    };

    img.onerror = (e) => {
      restoreConsole();
      e.preventDefault?.();
      e.stopPropagation?.();
      reject(new Error(`Failed to preload image: ${url}`));
    };

    img.src = url;
  });
};

/**
 * Preload multiple images with priority
 */
export const preloadImages = async (
  urls: string[],
  maxConcurrent: number = 3
): Promise<void> => {
  const chunks = [];
  for (let i = 0; i < urls.length; i += maxConcurrent) {
    chunks.push(urls.slice(i, i + maxConcurrent));
  }

  for (const chunk of chunks) {
    await Promise.allSettled(chunk.map(preloadImage));
  }
};

export default {
  getOptimizedImageUrl,
  getResponsiveImageUrls,
  getAdaptiveImageUrl,
  preloadImage,
  preloadImages,
  isMobileDevice,
  isSlowConnection
};

